import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/cn';

export type SortOrder = 'asc' | 'desc';

export interface SortableHeaderProps<F extends string = string> {
  /** The sort field key this header controls. */
  field: F;
  /** The currently active sort field on the table. */
  current: F;
  /** Direction of the current sort. */
  order: SortOrder;
  /** Called when the user clicks the header. Toggles direction if the
   *  field is already active; otherwise switches to this field with the
   *  given `defaultOrder`. */
  onSort: (next: { sort: F; order: SortOrder }) => void;
  /** Direction used the first time this column becomes active. Defaults
   *  to `asc` for text and `desc` for numeric/date columns. */
  defaultOrder?: SortOrder;
  align?: 'left' | 'right' | 'center';
  className?: string;
  children: React.ReactNode;
}

/**
 * A `<th>`-style header that toggles its column's sort on click. Renders
 * a chevron when the column is active and a neutral up/down icon when
 * it's not, so users can see at a glance which column the table is
 * currently sorted by.
 *
 * Drop in inside a `<thead><tr>` exactly like a `<th>`.
 */
export function SortableHeader<F extends string = string>({
  field,
  current,
  order,
  onSort,
  defaultOrder = 'asc',
  align = 'left',
  className,
  children,
}: SortableHeaderProps<F>) {
  const isActive = current === field;
  const handleClick = () => {
    if (isActive) {
      onSort({ sort: field, order: order === 'asc' ? 'desc' : 'asc' });
    } else {
      onSort({ sort: field, order: defaultOrder });
    }
  };

  const Icon = !isActive ? ChevronsUpDown : order === 'asc' ? ChevronUp : ChevronDown;

  const alignClass =
    align === 'right'
      ? 'justify-end text-right'
      : align === 'center'
        ? 'justify-center text-center'
        : 'justify-start text-left';

  return (
    <th
      className={cn('px-3 py-2 text-xs uppercase font-semibold', className)}
    >
      <button
        type="button"
        onClick={handleClick}
        className={cn(
          'group flex w-full items-center gap-1 transition-colors',
          alignClass,
          isActive
            ? 'text-forest-700 dark:text-forest-300'
            : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
        )}
      >
        <span>{children}</span>
        <Icon
          className={cn(
            'h-3.5 w-3.5 flex-shrink-0',
            isActive ? 'opacity-100' : 'opacity-40 group-hover:opacity-70',
          )}
        />
      </button>
    </th>
  );
}
